import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CoffeeChatLayout from '../../components/coffee-chat/CoffeeChatLayout';
import { fetchLecturers, Lecturer } from '../../services/lecturerApi';
import { getLecturerImageWithFallback } from '../../utils/imageUtils';
import { searchLecturersAdvanced, LecturerSearchItem } from '../../services/searchApi';
import { coffeeChatBannerApi, CoffeeChatBanner } from '../../services';
import { fetchCoffeeChatCurations, CoffeeChatCurationSection, validateCoffeeChatCurationSection } from '../../services/coffeeChatCurationsApi';

// 디바운스 훅
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// 커피챗 카드 타입 (Lecturer 인터페이스 활용)
interface CoffeeChatCard {
  id: string;
  category: string;
  title: string;
  description: string;
  imageUrl: string;
  tags?: string[];
}

const CoffeeChatHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  
  // 디바운스된 검색어 (500ms 지연)
  const debouncedSearchValue = useDebounce(searchValue, 500);
  
  // 배너 데이터 상태 관리
  const [bannerData, setBannerData] = useState<CoffeeChatBanner[]>([]);
  const [bannerLoading, setBannerLoading] = useState(true);
  
  // 배너 슬라이드 상태 관리
  const [bannerPage, setBannerPage] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // 배너 드래그 상태 관리
  const [bannerDrag, setBannerDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0
  });
  
  // 강연자 데이터 상태 관리
  const [connectWorldLecturers, setConnectWorldLecturers] = useState<Lecturer[]>([]);
  const [allLecturers, setAllLecturers] = useState<Lecturer[]>([]);
  
  // 커피챗 큐레이션 데이터 상태 관리
  const [coffeeChatCurations, setCoffeeChatCurations] = useState<CoffeeChatCurationSection[]>([]);
  const [curationsLoading, setCurationsLoading] = useState(false);
  const [curationPages, setCurationPages] = useState<{ [key: number]: number }>({}); // 각 큐레이션별 현재 페이지 상태
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<LecturerSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // 무한스크롤 관련 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 16;
  
  // 이미지 로딩 상태 관리
  const [imageLoadStates, setImageLoadStates] = useState<{[key: string]: boolean}>({});

  // 큐레이션 드래그 상태 (각 큐레이션별로 분리)
  const [curationDrags, setCurationDrags] = useState<{ [key: number]: {
    isDragging: boolean;
    startX: number;
    currentX: number;
    offset: number;
    hasMoved: boolean;
    preventClick: boolean;
  }}>({});

  // 컨테이너 참조
  const curationRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // 동적 배너 데이터 계산
  const totalBannersCount = bannerData.length;
  
  // 무한 슬라이드를 위한 확장된 배너 데이터
  const extendedBannerData = totalBannersCount > 0 ? [
    bannerData[bannerData.length - 1], // 마지막 배너를 앞에 추가
    ...bannerData,
    bannerData[0] // 첫 번째 배너를 뒤에 추가
  ] : [];

  // 현재 실제 페이지 번호 계산
  const getCurrentBannerPage = () => {
    if (bannerPage === 0) return totalBannersCount;
    if (bannerPage === totalBannersCount + 1) return 1;
    return bannerPage;
  };

  // 배너 페이지 변경 핸들러 (무한 슬라이드) - 배너가 2개 이상일 때만
  const handleBannerPageChange = useCallback((direction: 'prev' | 'next') => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 페이지 변경 비활성화
    
    setIsTransitioning(true);
    
    if (direction === 'prev') {
      if (bannerPage === 1) {
        setBannerPage(bannerPage - 1); // 0으로 이동 (복사본)
      } else {
        setBannerPage(bannerPage - 1);
      }
    } else {
      if (bannerPage === totalBannersCount) {
        setBannerPage(bannerPage + 1); // totalBanners + 1로 이동 (복사본)
      } else {
        setBannerPage(bannerPage + 1);
      }
    }
  }, [bannerPage, totalBannersCount]);

  // 배너 드래그 핸들러
  const handleBannerDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 드래그 비활성화
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    setBannerDrag({
      isDragging: true,
      startX: clientX,
      currentX: clientX,
      offset: 0
    });
  }, [totalBannersCount]);

  const handleBannerDragEnd = useCallback(() => {
    if (bannerDrag.isDragging && totalBannersCount > 1) {
      const dragDistance = bannerDrag.currentX - bannerDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold) {
        handleBannerPageChange('prev');
      } else if (dragDistance < -threshold) {
        handleBannerPageChange('next');
      }
      
      setBannerDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0
      });
    }
  }, [bannerDrag, totalBannersCount, handleBannerPageChange]);

  // 자동 슬라이드 효과 (15초마다) - 배너가 2개 이상일 때만
  useEffect(() => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 자동 슬라이드 비활성화
    
    const interval = setInterval(() => {
      if (!bannerDrag.isDragging) {
        handleBannerPageChange('next');
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [bannerDrag.isDragging, handleBannerPageChange, totalBannersCount]);

  // 무한 슬라이드 처리를 위한 useEffect - 배너가 2개 이상일 때만
  useEffect(() => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 무한 슬라이드 비활성화
    
    if (bannerPage === 0) {
      // 첫 번째 복사본에서 실제 마지막으로 이동
      setTimeout(() => {
        setIsTransitioning(false);
        setBannerPage(totalBannersCount);
      }, 600);
    } else if (bannerPage === totalBannersCount + 1) {
      // 마지막 복사본에서 실제 첫 번째로 이동
      setTimeout(() => {
        setIsTransitioning(false);
        setBannerPage(1);
      }, 600);
    }
  }, [bannerPage, totalBannersCount]);

  // 전역 마우스 이벤트 핸들러 (배너만)
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (bannerDrag.isDragging) {
        const offset = e.clientX - bannerDrag.startX;
        setBannerDrag(prev => ({
          ...prev,
          currentX: e.clientX,
          offset: offset
        }));
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (bannerDrag.isDragging) {
        const offset = e.touches[0].clientX - bannerDrag.startX;
        setBannerDrag(prev => ({
          ...prev,
          currentX: e.touches[0].clientX,
          offset: offset
        }));
      }
    };

    const handleGlobalMouseUp = () => {
      if (bannerDrag.isDragging) {
        handleBannerDragEnd();
      }
    };

    if (bannerDrag.isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove);
      document.addEventListener('touchend', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [bannerDrag, handleBannerDragEnd]);

  // 배너 데이터 호출 함수
  const fetchBannerData = async () => {
    try {
      setBannerLoading(true);
      
      const banners = await coffeeChatBannerApi.getCoffeeChatBanners();
      setBannerData(banners);
      
      // 배너가 없으면 기본 배너 사용
      if (banners.length === 0) {
        setBannerData([]);
      }
    } catch (err) {
      /// console.error('커피챗 배너 데이터 로딩 실패:', err);
      
      // 에러 발생 시 기본 배너 사용
      setBannerData([{
        banner_id: 1,
        title: '',
        description: '',
        font_color_hex_code: '#FFFFFF',
        banner_image_url: '/images/ccf.png'
      }]);
    } finally {
      setBannerLoading(false);
    }
  };

  // 커피챗 큐레이션 데이터 호출 함수
  const loadCoffeeChatCurations = async () => {
    setCurationsLoading(true);
    try {
      const response = await fetchCoffeeChatCurations();
      /// console.log('커피챗 큐레이션 데이터 응답:', {response});
      if (response.code === 200 && Array.isArray(response.data)) {
        const validatedCurations = response.data.map(validateCoffeeChatCurationSection);
        setCoffeeChatCurations(validatedCurations);
        /// console.log('커피챗 큐레이션 데이터 로드 완료:', validatedCurations);
      }
    } catch (error) {
      /// console.error('커피챗 큐레이션 데이터 로딩 실패:', error);
    } finally {
      setCurationsLoading(false);
    }
  };

  // 강연자 데이터 API 호출
  useEffect(() => {
    const loadLecturers = async () => {
      try {
        // CONNECT TO THE WORLD 섹션용 - page=0, size=4 (한 줄에 4개만 필요)
        const connectWorldResponse = await fetchLecturers({ page: 0, size: 4 });
        /// console.log('CONNECT TO THE WORLD 강연자 수:', connectWorldResponse.data.content.length); // 디버깅용
        setConnectWorldLecturers(connectWorldResponse.data.content);
        
        // 첫 페이지 전체 출력용 - page=0, size=16
        await loadAllLecturers(0, false);
        
      } catch (error) {
        /// console.error('강연자 데이터 로딩 실패:', error);
        // 에러 발생 시 빈 배열로 설정
        setConnectWorldLecturers([]);
        setAllLecturers([]);
      }
    };
    
    if (!isSearchMode) {
      loadLecturers();
    }
  }, [isSearchMode]);

  // 배너 데이터 로드
  useEffect(() => {
    fetchBannerData();
  }, []);

  // 커피챗 큐레이션 데이터 로드
  useEffect(() => {
    loadCoffeeChatCurations();
  }, []);

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams]);

  // 디바운스된 검색어가 변경될 때 검색 실행 (URL 파라미터가 아닌 직접 입력의 경우만)
  useEffect(() => {
    // URL에 키워드가 있으면 디바운스 검색 무시
    const urlKeyword = searchParams.get('keyword');
    if (urlKeyword) return;
    
    if (debouncedSearchValue.trim()) {
      setIsSearchMode(true);
      performSearch(debouncedSearchValue);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [debouncedSearchValue, searchParams]);

  // 검색 실행 함수
  const performSearch = async (keyword: string, page: number = 0, size: number = 20) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      // 새로운 API를 사용하여 페이징 지원
      const results = await searchLecturersAdvanced(keyword, undefined, page, size);
      setSearchResults(results);
    } catch (error) {
      /// console.error('강연자 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 전체 강연자 데이터 로드 함수 (무한스크롤용)
  const loadAllLecturers = async (page: number, isLoadMore: boolean = false) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      }
      
      const response = await fetchLecturers({ page, size: PAGE_SIZE });
      const lecturers = response.data.content;
      
      if (isLoadMore) {
        // 더 로드하는 경우 기존 데이터에 추가
        setAllLecturers(prevData => [...prevData, ...lecturers]);
      } else {
        // 초기 로드인 경우 새 데이터로 설정
        setAllLecturers(lecturers);
      }
      
      // 더 로드할 데이터가 있는지 확인
      const totalPages = response.data.totalPages;
      setHasMoreData(page + 1 < totalPages);
      setCurrentPage(page);
      
    } catch (error) {
      /// console.error('전체 강연자 데이터 로딩 실패:', error);
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      }
    }
  };

  // 강연자 데이터를 카드 형태로 변환
  const lecturerToCard = (lecturer: Lecturer): CoffeeChatCard => ({
    id: lecturer.id.toString(),
    category: '강연자',
    tags: lecturer.tags || [],
    title: lecturer.name,
    description: lecturer.introduction,
    imageUrl: getLecturerImageWithFallback(lecturer.image_url, lecturer.id)
  });

  // 검색 결과 강연자 데이터를 카드 형태로 변환
  const searchLecturerToCard = (lecturer: LecturerSearchItem): CoffeeChatCard => ({
    id: lecturer.id.toString(),
    category: '강연자',
    tags: lecturer.tags || [], // 새로운 API에서 tags 필드 사용
    title: lecturer.name,
    description: lecturer.department || '',
    imageUrl: getLecturerImageWithFallback(lecturer.image_url, lecturer.id)
  });

  // 가로 슬라이드 데이터 (4개만 한 줄로)
  const getHorizontalCards = () => {
    if (connectWorldLecturers.length === 0) {
      return [];
    }
    
    // 강제로 4개만 자르기
    const cards = connectWorldLecturers.slice(0, 4).map(lecturerToCard);
    /// console.log('CONNECT TO THE WORLD 카드 수:', cards.length); // 디버깅
    return cards;
  };
  
  const horizontalCards = getHorizontalCards();

  // 전체 카드 데이터 (검색 모드에 따라 다른 데이터 사용)
  const allCardsData = isSearchMode 
    ? searchResults.map(searchLecturerToCard)
    : allLecturers.map(lecturerToCard);

  // 이미지 로드 핸들러
  const handleImageLoad = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: true
    }));
  };

  // 이미지 에러 핸들러
  const handleImageError = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: false
    }));
  };

  const handleCardClick = (cardId: string) => {
    // 강연자 ID를 기반으로 상세 페이지로 이동
    navigate(`/coffee-chat/${cardId}`);
  };

  // 큐레이션 카드 클릭 핸들러 (드래그와 구분)
  const handleCurationCardClick = (personId: number, curationIndex: number, e: React.MouseEvent) => {
    const curationDrag = curationDrags[curationIndex];
    if (curationDrag && (curationDrag.hasMoved || curationDrag.preventClick || curationDrag.isDragging)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    navigate(`/coffee-chat/${personId}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    
    // URL 파라미터를 지우고 실시간 검색 모드로 전환
    if (searchParams.get('keyword')) {
      navigate('/coffee-chat', { replace: true });
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/coffee-chat?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/coffee-chat');
      }
    }
  };

  // 큐레이션 페이지 변경 함수
  const handleCurationPageChange = useCallback((curationIndex: number, direction: 'prev' | 'next') => {
    const curation = coffeeChatCurations[curationIndex];
    if (!curation) return;
    
    const currentPage = curationPages[curationIndex] || 0;
    const itemsPerPage = 4; // 한 페이지에 4개씩
    const totalItems = curation.related_persons.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    if (direction === 'prev' && currentPage > 0) {
      setCurationPages(prev => ({
        ...prev,
        [curationIndex]: currentPage - 1
      }));
    } else if (direction === 'next' && currentPage < totalPages - 1) {
      setCurationPages(prev => ({
        ...prev,
        [curationIndex]: currentPage + 1
      }));
    }
  }, [coffeeChatCurations, curationPages]);

  // 큐레이션 드래그 핸들러들
  const handleCurationDragStart = useCallback((e: React.MouseEvent | React.TouchEvent, curationIndex: number) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    setCurationDrags(prev => ({
      ...prev,
      [curationIndex]: {
        isDragging: true,
        startX: clientX,
        currentX: clientX,
        offset: 0,
        hasMoved: false,
        preventClick: false
      }
    }));
  }, []);

  const handleCurationDragMove = useCallback((e: React.MouseEvent | React.TouchEvent, curationIndex: number) => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const curationDrag = curationDrags[curationIndex];
    
    if (curationDrag && curationDrag.isDragging) {
      const offset = clientX - curationDrag.startX;
      const hasMoved = Math.abs(offset) > 5;
      
      setCurationDrags(prev => ({
        ...prev,
        [curationIndex]: {
          ...prev[curationIndex],
          currentX: clientX,
          offset: offset,
          hasMoved: hasMoved || prev[curationIndex].hasMoved
        }
      }));
    }
  }, [curationDrags]);

  const handleCurationDragEnd = useCallback((curationIndex: number) => {
    const curationDrag = curationDrags[curationIndex];
    
    if (curationDrag && curationDrag.isDragging) {
      const dragDistance = curationDrag.currentX - curationDrag.startX;
      const threshold = 100;
      const currentPage = curationPages[curationIndex] || 0;
      const curation = coffeeChatCurations[curationIndex];
      
      if (curation) {
        const itemsPerPage = 4;
        const totalItems = curation.related_persons.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        
        if (dragDistance > threshold && currentPage > 0) {
          handleCurationPageChange(curationIndex, 'prev');
        } else if (dragDistance < -threshold && currentPage < totalPages - 1) {
          handleCurationPageChange(curationIndex, 'next');
        }
      }
      
      const shouldPreventClick = curationDrag.hasMoved;
      
      setCurationDrags(prev => ({
        ...prev,
        [curationIndex]: {
          isDragging: false,
          startX: 0,
          currentX: 0,
          offset: 0,
          hasMoved: false,
          preventClick: shouldPreventClick
        }
      }));
      
      if (shouldPreventClick) {
        setTimeout(() => {
          setCurationDrags(prev => ({
            ...prev,
            [curationIndex]: {
              ...prev[curationIndex],
              preventClick: false
            }
          }));
        }, 200);
      }
    }
  }, [curationDrags, curationPages, coffeeChatCurations, handleCurationPageChange]);

  // 무한스크롤 구현
  useEffect(() => {
    const handleScroll = () => {
      // 페이지 하단에서 200px 위에 도달하면 더 로드
      if (
        window.innerHeight + document.documentElement.scrollTop 
        >= document.documentElement.offsetHeight - 200
      ) {
        if (!isLoadingMore && hasMoreData) {
          loadAllLecturers(currentPage + 1, true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, hasMoreData, isLoadingMore]);

  return (
    <CoffeeChatLayout>
      <Container>
        <MainContainer>
          {/* 상단 배너 이미지 */}
          <BannerSection>
            {bannerLoading ? (
              // 배너 로딩 중일 때 스켈레톤
              <BannerSlideWrapper>
                <BannerSlide style={{ width: '100%' }}>
                  <BannerContainer>
                    <BannerThumbnail>
                      <BannerImageSkeleton />
                    </BannerThumbnail>
                  </BannerContainer>
                </BannerSlide>
              </BannerSlideWrapper>
            ) : bannerData.length > 0 ? (
              <>
                <BannerSlideWrapper 
                  onMouseDown={(e) => handleBannerDragStart(e)}
                  onTouchStart={(e) => handleBannerDragStart(e)}
                  style={{ 
                    transform: `translateX(calc(-${(bannerPage) * (100/(totalBannersCount + 2))}% + ${bannerDrag.isDragging ? bannerDrag.offset : 0}px))`,
                    transition: bannerDrag.isDragging || !isTransitioning ? 'none' : 'transform 0.6s ease',
                    width: `${(totalBannersCount + 2) * 100}%`
                  }}
                >
                  {extendedBannerData.map((banner, index) => (
                    <BannerSlide key={`${banner.banner_id}-${index}`} style={{ width: `${100/(totalBannersCount + 2)}%` }}>
                      <BannerContainer>
                        <BannerThumbnail>
                          {!imageLoadStates[`banner-${banner.banner_id}-${index}`] && (
                            <BannerImageSkeleton />
                          )}
                          <img 
                            src={banner.banner_image_url} 
                            alt={banner.description}
                            style={{ 
                              display: imageLoadStates[`banner-${banner.banner_id}-${index}`] ? 'block' : 'none' 
                            }}
                            onLoad={() => handleImageLoad(`banner-${banner.banner_id}-${index}`)}
                            onError={() => handleImageError(`banner-${banner.banner_id}-${index}`)}
                          />
                        </BannerThumbnail>
                        {/* 배너 텍스트 */}
                        <BannerText>
                          <BannerTitle style={{ color: banner.font_color_hex_code || '#2d160a' }}>
                            {banner.title}
                          </BannerTitle>
                          <BannerDescription style={{ color: banner.font_color_hex_code || '#2d160a' }}>
                            {banner.description}
                          </BannerDescription>
                        </BannerText>
                      </BannerContainer>
                    </BannerSlide>
                  ))}
                </BannerSlideWrapper>
                
                {/* 페이지네이션 인디케이터 - 배너가 2개 이상일 때만 표시 */}
                {totalBannersCount > 1 && (
                  <BannerPagination>
                    {bannerData.map((_, index) => (
                      <PaginationDot 
                        key={index}
                        active={getCurrentBannerPage() === index + 1}
                        onClick={() => {
                          setIsTransitioning(true);
                          setBannerPage(index + 1);
                        }}
                      />
                    ))}
                  </BannerPagination>
                )}
              </>
            ) : (
              <div style={{ 
                height: '200px', 
                backgroundColor: '#f5f5f5', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: '12px',
                color: 'rgba(55, 56, 60, 0.61)'
              }}>
                배너 데이터를 불러올 수 없습니다
              </div>
            )}
          </BannerSection>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커피챗</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력하세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 커피챗 큐레이션 섹션들 - 검색 모드가 아닐 때만 표시 */}
          {!isSearchMode && coffeeChatCurations.map((curation, curationIndex) => {
            const currentPage = curationPages[curationIndex] || 0;
            const itemsPerPage = 4; // 한 페이지에 4개씩
            const totalItems = curation.related_persons.length;
            const totalPages = Math.ceil(totalItems / itemsPerPage);
            const curationDrag = curationDrags[curationIndex] || {
              isDragging: false,
              startX: 0,
              currentX: 0,
              offset: 0,
              hasMoved: false,
              preventClick: false
            };

            /// console.log('curation', curation);
            return (
              <CurationSection key={curation.coffee_chat_section_id}>
                <CurationSectionHeader>
                  <CurationSectionTitleGroup>
                    <CurationSectionTitle>{curation.title}</CurationSectionTitle>
                    <CurationSectionCaption>{curation.text}</CurationSectionCaption>
                  </CurationSectionTitleGroup>
                  {totalPages > 1 && (
                    <CurationSectionTrailing>
                      <CurationPaginationButtons>
                        <CurationPaginationContainer>
                          <CurationPaginationContent>
                            <CurationPaginationBtn 
                              onClick={() => handleCurationPageChange(curationIndex, 'prev')}
                              disabled={currentPage === 0}
                            >
                              <CurationPaginationIcon isActive={currentPage > 0}>
                                <img src="/images/left.svg" alt="Previous" />
                              </CurationPaginationIcon>
                            </CurationPaginationBtn>
                            <CurationPaginationBtn 
                              onClick={() => handleCurationPageChange(curationIndex, 'next')}
                              disabled={currentPage >= totalPages - 1}
                            >
                              <CurationPaginationIcon isRight isActive={currentPage < totalPages - 1}>
                                <img src="/images/left.svg" alt="Next" />
                              </CurationPaginationIcon>
                            </CurationPaginationBtn>
                          </CurationPaginationContent>
                        </CurationPaginationContainer>
                      </CurationPaginationButtons>
                    </CurationSectionTrailing>
                  )}
                </CurationSectionHeader>
                <CurationSlideContainer
                  ref={(el) => { curationRefs.current[curationIndex] = el; }}
                  onMouseDown={(e) => handleCurationDragStart(e, curationIndex)}
                  onMouseMove={(e) => handleCurationDragMove(e, curationIndex)}
                  onTouchStart={(e) => handleCurationDragStart(e, curationIndex)}
                  onTouchMove={(e) => handleCurationDragMove(e, curationIndex)}
                  onMouseUp={() => handleCurationDragEnd(curationIndex)}
                  onTouchEnd={() => handleCurationDragEnd(curationIndex)}
                  onMouseLeave={() => handleCurationDragEnd(curationIndex)}
                  style={{ 
                    pointerEvents: curationDrag.isDragging && curationDrag.hasMoved ? 'none' : 'auto',
                    userSelect: 'none'
                  }}
                >
                  {curationsLoading ? (
                    // 스켈레톤 UI
                    <CurationCardGrid>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <CurationCard 
                          key={`skeleton-${index}`}
                          style={{ pointerEvents: 'none' }}
                        >
                          <CurationCardImageWrapper>
                            <CurationCardImage>
                              <CurationCardImageSkeleton />
                            </CurationCardImage>
                          </CurationCardImageWrapper>
                          <CurationCardContent style={{ gap: '4px' }}>
                            <CurationCardBadgeContainer>
                              <CurationCardBadgeSkeleton />
                            </CurationCardBadgeContainer>
                            <CurationCardTextContent>
                              <CurationCardTitleSkeleton />
                              <CurationCardSubtitleSkeleton />
                            </CurationCardTextContent>
                          </CurationCardContent>
                        </CurationCard>
                      ))}
                    </CurationCardGrid>
                  ) : totalPages > 1 ? (
                    <CurationSlideWrapper style={{ 
                      transform: `translateX(calc(-${currentPage * (100/totalPages)}% - ${currentPage * 24}px + ${curationDrag.isDragging ? curationDrag.offset : 0}px))`,
                      transition: curationDrag.isDragging ? 'none' : 'transform 0.6s ease',
                      width: `${totalPages * 100}%`
                    }}>
                      {Array.from({ length: totalPages }).map((_, pageIndex) => (
                        <CurationSlidePage key={pageIndex} style={{ width: `${100/totalPages}%` }}>
                          <CurationCardGrid>
                            {curation.related_persons
                              .slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)
                              .map((person) => (
                                <CurationCard 
                                  key={person.section_related_person_id}
                                  onClick={(e) => handleCurationCardClick(person.person_id, curationIndex, e)}
                                >
                                  <CurationCardImageWrapper>
                                    <CurationCardImage>
                                      <CurationCardBg 
                                        style={{backgroundImage: person.image_url ? `url('${person.image_url}')` : 'none'}} 
                                      />
                                    </CurationCardImage>
                                  </CurationCardImageWrapper>
                                  <CurationCardContent style={{ gap: person.tags && person.tags.length > 0 ? '4px' : '0' }}>
                                    {person.tags && person.tags.length > 0 && (
                                      <CurationCardBadgeContainer>
                                        {person.tags.map((tag) => (
                                          <CurationCardBadge key={tag}>{tag}</CurationCardBadge>
                                        ))}
                                      </CurationCardBadgeContainer>
                                    )}
                                    <CurationCardTextContent>
                                      <CurationCardTitle>{person.name}</CurationCardTitle>
                                      <CurationCardSubtitle>{person.department?.replace(/\s*\|\s*강연자\s*$/, '')}</CurationCardSubtitle>
                                    </CurationCardTextContent>
                                  </CurationCardContent>
                                </CurationCard>
                              ))}
                            {/* 빈 공간 채우기 */}
                            {Array.from({ 
                              length: Math.max(0, itemsPerPage - curation.related_persons.slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage).length) 
                            }).map((_, emptyIndex) => (
                              <div key={`empty-${emptyIndex}`} style={{ visibility: 'hidden' }}>
                                <CurationCard style={{ pointerEvents: 'none' }}>
                                  <CurationCardImageWrapper>
                                    <CurationCardImage>
                                      <CurationCardBg />
                                    </CurationCardImage>
                                  </CurationCardImageWrapper>
                                  <CurationCardContent>
                                    <CurationCardTextContent>
                                      <div style={{ height: '24px' }} />
                                      <div style={{ height: '18px' }} />
                                    </CurationCardTextContent>
                                  </CurationCardContent>
                                </CurationCard>
                              </div>
                            ))}
                          </CurationCardGrid>
                        </CurationSlidePage>
                      ))}
                    </CurationSlideWrapper>
                  ) : (
                    // 한 페이지만 있을 때 (슬라이드 없음)
                    <CurationCardGrid>
                      {curation.related_persons.map((person) => (
                        <CurationCard 
                          key={person.section_related_person_id}
                          onClick={() => navigate(`/coffee-chat/${person.person_id}`)}
                        >
                          <CurationCardImageWrapper>
                            <CurationCardImage>
                              <CurationCardBg 
                                style={{backgroundImage: person.image_url ? `url('${person.image_url}')` : 'none'}} 
                              />
                            </CurationCardImage>
                          </CurationCardImageWrapper>
                          <CurationCardContent style={{ gap: person.tags && person.tags.length > 0 ? '4px' : '0' }}>
                            {person.tags && person.tags.length > 0 && (
                              <CurationCardBadgeContainer>
                                {person.tags.slice(0, 1).map((tag) => (
                                  <CurationCardBadge key={tag}>{tag}</CurationCardBadge>
                                ))}
                              </CurationCardBadgeContainer>
                            )}
                            <CurationCardTextContent>
                              <CurationCardTitle>{person.name}</CurationCardTitle>
                              <CurationCardSubtitle>{person.department?.replace(/\s*\|\s*강연자\s*$/, '')}</CurationCardSubtitle>
                            </CurationCardTextContent>
                          </CurationCardContent>
                        </CurationCard>
                      ))}
                    </CurationCardGrid>
                  )}
                </CurationSlideContainer>
              </CurationSection>
            );
          })}

          {/* 검색 모드일 때만 카드 그리드 섹션 표시 */}
          {isSearchMode && (
            <>
              {/* 검색 모드일 때 제목 표시 */}
              <SearchResultHeader>
                <SearchResultTitle>'{searchValue}' 검색 결과</SearchResultTitle>
              </SearchResultHeader>

              {/* 카드 그리드 섹션들 */}
              <CardSection>
                {searchLoading ? (
                  <LoadingState>검색 중...</LoadingState>
                ) : searchError ? (
                  <EmptyState>{searchError}</EmptyState>
                ) : searchResults.length === 0 ? (
                  <EmptyState>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyState>
                ) : (
                  <CardGrid>
                    {allCardsData.map((card) => (
                      <Card 
                        key={card.id}
                        onClick={() => handleCardClick(card.id)}
                      >
                        <CardImage>
                          {!imageLoadStates[`card-${card.id}`] && (
                            <CardImageSkeleton />
                          )}
                          <img 
                            src={card.imageUrl} 
                            alt={card.title}
                            style={{ 
                              display: imageLoadStates[`card-${card.id}`] ? 'block' : 'none' 
                            }}
                            onLoad={() => handleImageLoad(`card-${card.id}`)}
                            onError={() => handleImageError(`card-${card.id}`)}
                          />
                        </CardImage>
                        <CardContent style={{ gap: card?.tags && card.tags.length > 0 ? '4px' : '0' }}>
                          {card?.tags && card.tags.length > 0 && (
                            <BadgeContainer>
                              {card.tags.map((tag) => (
                                <Badge key={tag}>{tag}</Badge>
                              ))}
                            </BadgeContainer>
                          )}
                          <CardTextContent>
                            <CardTitle>{card.title}</CardTitle>
                            <CardDescription>{card.description}</CardDescription>
                          </CardTextContent>
                        </CardContent>
                      </Card>
                    ))}
                  </CardGrid>
                )}
              </CardSection>
            </>
          )}
        </MainContainer>
      </Container>
    </CoffeeChatLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

// 가로 슬라이드 섹션 스타일 (메인페이지와 동일)
const HorizontalSlideSection = styled.div`
  width: 100%;
  margin-bottom: 0;
  margin-top: -8px; /* 32px gap에서 8px 줄여서 24px 간격으로 조정 */
`;

const SlideContainer = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  
  &:active {
    cursor: grabbing;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  width: 100%; /* 1페이지만 사용하므로 100% */
  transition: none; /* 페이징이 없으므로 애니메이션 비활성화 */
  will-change: auto;
`;

const SlidePage = styled.div`
  width: 100%; /* 1페이지만 사용하므로 전체 너비 */
  flex-shrink: 0;
`;

const HorizontalCardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 1fr; /* 한 줄 고정 */
  gap: 24px;
  align-items: start;
  overflow: hidden; /* 넘치는 카드 숨기기 */
  max-height: 300px; /* 최대 높이 제한 */
  
  /* 4개 초과시 숨기기 */
  > *:nth-child(n+5) {
    display: none;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    > *:nth-child(n+4) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    > *:nth-child(n+3) {
      display: none;
    }
  }
`;

// 커피챗 카드 컴포넌트 (가로 스크롤용)
const HorizontalCoffeeChatCard = styled.div`
  flex-direction: column;
  gap: 12px;
  display: flex;
  cursor: pointer;
  width: 100%; // 그리드 컬럼에 맞게 100% 너비 사용
  max-width: 282px; // 최대 너비는 제한
  flex-shrink: 0; // 축소 방지
`;

const HorizontalCardImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  min-height: 188px; /* 최소 높이 보장 */
`;

const HorizontalCardImage = styled.img`
  width: 100%; // 부모 컨테이너에 맞게 조정
  height: 188px;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
`;

const HorizontalCardBadge = styled.div`
  height: 20px;
  padding: 0 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP';
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
`;

const HorizontalCardContent = styled.div`
  flex-direction: column;
  gap: 4px;
  display: flex;
`;

const HorizontalCardTitle = styled.div`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP';
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const HorizontalCardCaption = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP';
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  padding-top: 24px;
  padding-bottom: 128px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  /* 각 섹션별 간격 조정을 위해 자식 요소에서 개별 margin 설정 */
`;

const BannerContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  aspect-ratio: 3/1; /* 1200:400 = 3:1 비율 */
  min-height: 200px; /* 최소 높이 보장 */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 48px;
  box-sizing: border-box;
  text-align: left;
  font-size: 56px;
  color: #2d160a;
  font-family: 'Pretendard JP', sans-serif;
  
  @media (max-width: 1024px) {
    min-height: 150px;
  }
  
  @media (max-width: 768px) {
    min-height: 120px;
    padding: 0px 24px;
  }
`;

const BannerThumbnail = styled.div`
  width: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  z-index: 1;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BannerText = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  z-index: 2;
  
  @media (max-width: 768px) {
    margin-left: 24px;
  }
`;

const BannerTitle = styled.b`
  position: relative;
  font-size: 56px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 128.6%;
  
  @media (max-width: 1024px) {
    font-size: 44px;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const BannerDescription = styled.div`
  position: relative;
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 136.4%;
  
  @media (max-width: 1024px) {
    font-size: 18px;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: auto; /* height를 auto로 변경하여 내용에 맞게 조정 */
  min-height: 32px;
  gap: 8px;
  z-index: 10;
  position: relative;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const HeaderSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-top: 32px; /* 브레드크럼과의 여백 */
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  margin: 0;
`;


const CardSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  
  &:first-of-type {
    margin-top: 24px; /* 구분선과의 여백 */
  }
`;

const CardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(6, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(8, 1fr);
    gap: 16px;
  }
`;

const Card = styled.div`
  flex: 1;
  max-width: 282px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
`;

const CardImage = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  min-height: 188px; /* 최소 높이 보장 */
  
  img {
    width: 100%;
    height: 188px;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
  }
`;

const CardContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const BadgeContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const Badge = styled.div`
  height: 20px;
  padding: 0 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
`;

const CardTextContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.div`
  width: 100%;
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const CardDescription = styled.div`
  width: 100%;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

// 스켈레톤 UI 컴포넌트들
const BannerImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const HorizontalCardImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CardImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 가로 슬라이드 카드 텍스트 스켈레톤
const HorizontalCardBadgeLoader = styled.div`
  height: 20px;
  width: 50px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const HorizontalCardTitleLoader = styled.div`
  height: 24px;
  width: 80%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const HorizontalCardCaptionLoader = styled.div`
  height: 18px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;


/* 검색 결과 헤더 */
const SearchResultHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
`;

const SearchResultTitle = styled.h2`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  margin: 0;
`;

/* 로딩 상태 */
const LoadingState = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

/* 빈 상태 */
const EmptyState = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

// 배너 슬라이드 관련 스타일 컴포넌트들
const BannerSection = styled.section`
  position: relative;
  width: 100%;
  margin-bottom: 0;
  overflow: hidden;
  border-radius: 12px;
  
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const BannerSlideWrapper = styled.div`
  display: flex;
  transition: transform 0.6s ease;
  will-change: transform;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  border-radius: 12px;
  
  &:active {
    cursor: grabbing;
  }
`;

const BannerSlide = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 0;
`;

const BannerPagination = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 3;
`;

const PaginationDot = styled.button<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

/* 큐레이션 섹션 스타일 컴포넌트들 */
const CurationSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 32px;
`;

const CurationSectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const CurationSectionTitleGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CurationSectionTitle = styled.h2`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.358;
  letter-spacing: -2.36%;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CurationSectionCaption = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.445;
  letter-spacing: -0.02%;
  color: rgba(55, 56, 60, 0.61);
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CurationSectionTrailing = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 38px;
  
  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

const CurationPaginationButtons = styled.div`
  height: 38px;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  display: inline-flex;
`;

const CurationPaginationContainer = styled.div`
  position: relative;
  border-radius: 10px;
  outline: 1px rgba(112, 115, 124, 0.16) solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const CurationPaginationContent = styled.div`
  overflow: hidden;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const CurationPaginationBtn = styled.button<{ disabled?: boolean }>`
  padding: 7px;
  justify-content: center;
  align-items: center;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &:disabled {
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(23, 23, 25, 0.04);
      border-radius: 6px;
    }
  }
  
  &:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const CurationPaginationIcon = styled.div<{ isRight?: boolean; isActive?: boolean }>`
  height: 18px;
  width: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: ${props => props.isRight ? 'rotate(180deg)' : 'none'};
  
  img {
    width: 18px;
    height: 18px;
    filter: ${props => props.isActive ? 'brightness(0) saturate(100%) invert(0%)' : 'opacity(0.35)'};
    transition: filter 0.2s ease;
  }
`;

const CurationSlideContainer = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  &:active {
    cursor: grabbing;
  }
  
  * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
`;

const CurationSlideWrapper = styled.div`
  display: flex;
  transition: transform 0.6s ease;
  will-change: transform;
`;

const CurationSlidePage = styled.div`
  flex-shrink: 0;
  
  &:not(:last-child) {
    margin-right: 24px;
  }
`;

const CurationCardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const CurationCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  width: 100%;
  max-width: 282px;
`;

const CurationCardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(112, 115, 124, 0.08);
`;

const CurationCardImage = styled.div`
  width: 100%;
  aspect-ratio: 3/2;
  position: relative;
  overflow: hidden;
`;

const CurationCardBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-color: #f5f5f5;
  transition: transform 0.3s ease;
  
  ${CurationCard}:hover & {
    transform: scale(1.05);
  }
`;

const CurationCardContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CurationCardBadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const CurationCardBadge = styled.div`
  height: 20px;
  padding: 0 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
`;

const CurationCardTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const CurationCardTitle = styled.h3`
  color: #171719;
  font-family: 'Pretendard JP', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const CurationCardSubtitle = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-family: 'Pretendard JP', sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  margin: 0;
`;

/* 큐레이션 스켈레톤 UI 컴포넌트들 */
const CurationCardImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CurationCardBadgeSkeleton = styled.div`
  width: 50px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
`;

const CurationCardTitleSkeleton = styled.div`
  width: 80%;
  height: 24px;
  background: #e0e0e0;
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite alternate;
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.8;
    }
  }
`;

const CurationCardSubtitleSkeleton = styled.div`
  width: 100%;
  height: 18px;
  background: #e0e0e0;
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite alternate;
  animation-delay: 0.2s;
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.8;
    }
  }
`;

export default CoffeeChatHome;